import { io, type Socket } from 'socket.io-client'

/**
 * The one push channel in this product — see `StaffEventsGateway` on the server.
 *
 * 🔴 WHY A SOCKET AND NOT A POLL. A school declaring a closure is the one event
 * whose value is that somebody finds out BEFORE they act on the old answer: a
 * teacher who learns on Tuesday morning that Tuesday is a holiday has already
 * travelled in. Everything else this tier shows is read when somebody chooses to
 * look, and polling it would be work with no reader.
 *
 * 🔴 THE FRAME IS A POINTER, NOT A PAYLOAD, AND THE SCREEN MUST TREAT IT THAT
 * WAY. It carries a headline and a route — never a child's name, never a figure.
 * A socket message arrives at a screen nobody is necessarily sitting in front of
 * (a staffroom laptop with the dashboard left open), so it says *"something
 * changed, here is where to look"* and the screen then makes its ordinary
 * scoped, authenticated request. Rendering a figure straight from a frame would
 * put unscoped data on a screen the server never authorised for it.
 *
 * ⚠️ IT IS NOT A DELIVERY GUARANTEE AND NOTHING MAY DEPEND ON IT. A shut laptop
 * misses the frame entirely; the holiday is still in the calendar the screen
 * loads, and the parent's WhatsApp message is queued through an outbox that
 * retries. This is the fast path, not the durable one.
 */

/** 🔴 A pointer, never a payload — see above. Mirrors `StaffEvent` on the server. */
export interface StaffEvent {
  /** e.g. `holiday.declared`. Namespaced so a screen can filter cheaply. */
  type: string
  title: string
  detail?: string
  /** ISO 8601. When the thing happened, not when the frame arrived. */
  at: string
}

/**
 * ⚠️ THE SAME BASE THE REST CLIENT USES, AND READ THE SAME WAY. Blank in dev, so
 * the socket goes to the current origin and through Vite's proxy; in production
 * the app and the API are the same origin. `client.ts` states the rule this
 * follows: *"sending a credential to whatever host an env var happened to name
 * is how a credential leaves the building"*, and this connection carries an
 * access token in its handshake.
 */
function baseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? ''
}

/**
 * Open the staff channel, and return the function that closes it.
 *
 * 🔴 THE TOKEN GOES IN `auth`, NEVER IN THE QUERY STRING. socket.io supports
 * both; a query parameter lands in access logs and in proxy logs, which is where
 * credentials go to be found later. The server reads `handshake.auth.token` and
 * disconnects anything it cannot verify.
 *
 * 🔴 THE CALLER GETS A DISPOSER RATHER THAN THE SOCKET. A component holding the
 * socket itself is a component that can emit on it, and this channel is one-way
 * by design — the server mounts no inbound handlers at all. Returning only the
 * teardown keeps the shape a `useEffect` wants and keeps the surface honest.
 *
 * ⚠️ RECONNECTION IS socket.io's OWN, DELIBERATELY LEFT ON. A classroom's
 * connection drops; the alternative is a channel that silently stops working
 * after the first blip and a staffroom that concludes the feature does not work.
 * Frames sent while disconnected are LOST — see the note above about this not
 * being a delivery guarantee.
 */
export function openStaffEvents(token: string, onEvent: (event: StaffEvent) => void): () => void {
  const socket: Socket = io(`${baseUrl()}/staff`, {
    auth: { token },
    /*
     * ⚠️ WEBSOCKET FIRST, WITH POLLING AS THE FALLBACK — socket.io's default,
     * stated rather than assumed. A school behind a proxy that strips upgrades
     * still gets the channel; without the fallback that school gets silence.
     */
    transports: ['websocket', 'polling'],
    withCredentials: true,
  })

  socket.on('staff.event', (event: StaffEvent) => {
    onEvent(event)
  })

  return () => {
    /* ⚠️ `off` BEFORE `disconnect`: a frame delivered during teardown would
       otherwise call into a component that has already unmounted. */
    socket.off('staff.event')
    socket.disconnect()
  }
}
